import { Icon, IImageProps, Image, ImageFit } from 'office-ui-fabric-react';
import React, { FunctionComponent, useCallback } from 'react';
import styled from 'styled-components';

import { SrOnlyText } from '../SrOnlyText/SrOnlyText';
import { BaseCard } from '../BaseCard/BaseCard';
import { noCardBoxShadow } from '../../Styles';
import { body1Font, caption2Font } from '../Typography/Typography';

export interface IPersonalHistoryRecordProps {
  title: string;
  subtitle?: string | string[];
  link?: string;
  iconName?: string;
  imageUrl?: string;
  screenReaderText?: string;
  onCardClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const renderImage = (props: IPersonalHistoryRecordProps): JSX.Element => {
  const { imageUrl, iconName = 'CityNext' } = props;

  const imageProps: IImageProps = {
    src: imageUrl,
    imageFit: ImageFit.contain,
    styles: {
      root: {
        width: '40px',
        height: '40px',
      },
    },
  };

  if (!imageUrl || imageUrl === 'about:blank') {
    return <Icon iconName={iconName} styles={{ root: { fontSize: '40px', margin: '15px 15px 0 0' } }} />;
  } else {
    return <Image role="presentation" {...imageProps} />;
  }
};

const SubTitle = styled.div`
  color: rgb(102, 102, 102);
  ${caption2Font}
  margin: 0 0 2px 0;
`;

const renderSubtitle = (props: IPersonalHistoryRecordProps): JSX.Element => {
  let { subtitle = [] } = props;

  if (!Array.isArray(subtitle)) {
    subtitle = [subtitle];
  }

  const Elems = subtitle.map((subtitleText: string) => <SubTitle key={subtitleText}>{subtitleText}</SubTitle>);

  return <>{Elems}</>;
};

const StyledBaseCard = styled(BaseCard)`
  display: flex;
  margin-bottom: 5px;
  font-weight: 500;
  position: relative;
  left: -10px;
  padding: 10px;
  ${noCardBoxShadow}
`;

const StyledImgContainer = styled.div`
  width: 40px;
  margin-right: 20px;
  i {
    margin: 0;
  }
`;

const InfoContainer = styled.div``;

const Title = styled.h2`
  ${body1Font}
  color: rgb(51, 51, 51);
  font-weight: normal;
  margin: 0 0 2px 0;
`;

export const PersonalHistoryRecord: FunctionComponent<IPersonalHistoryRecordProps> = props => {
  const { title, link, screenReaderText, onCardClick } = props;

  const onRecordClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (link) {
        window.open(link, '_blank');
      }
      if (onCardClick) {
        onCardClick(event);
      }
    },
    [link, onCardClick]
  );

  return (
    <StyledBaseCard onCardClick={onRecordClick} screenReaderText="External link to LinkedIn">
      <StyledImgContainer>{renderImage(props)}</StyledImgContainer>
      <InfoContainer>
        {screenReaderText && <SrOnlyText>{screenReaderText}</SrOnlyText>}
        <Title className="highlight-on-card-hover">{title}</Title>
        {renderSubtitle(props)}
      </InfoContainer>
    </StyledBaseCard>
  );
};

export default PersonalHistoryRecord;
